<template>
  <div class="mini-course-moments moments-table-wrapper">
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Startmomenten worden geladen...</p>
    </div>
    <div v-else-if="hasError" class="error-container">
      <p class="error-message">{{ errorMessage }}</p>
      <button class="retry-button" @click="$emit('retry')">Opnieuw proberen</button>
    </div>
    <div v-else>
      <!-- Desktop table view -->
      <table class="moments-table desktop-only">
        <thead>
          <tr>
            <th>Datum</th>
            <th>Tijd</th>
            <th class="location-column">Locatie</th>
            <th>Prijs</th>
            <th>Beschikbare plaatsen</th>
            <!-- <th>Startgarantie</th> -->
            <th></th> 
          </tr>
        </thead>
        <tbody>
          <tr v-for="moment in moments" :key="moment.id">
            <td>{{ formatDate(moment.start_date) }}</td>
            <td>{{ moment.start_time || '--:--' }}</td>
            <td class="location-column">{{ moment.city || moment.location || 'N.v.t.' }}</td>
            <td>
              <div class="price-with-badge">
                <template v-if="moment.price?.discount && moment.price?.original">
                  <span class="original-price">{{ formatPrice(moment.price.original) }}</span>
                  <span class="discounted-price">{{ formatPrice(moment.price.total) }}</span>
                  <span class="discount-badge">ACTIE</span>
                </template>
                <template v-else>
                  {{ formatPrice(moment.price) }}
                </template>
              </div>
            </td>
            <td>{{ formatAvailableSpots(moment.available_spots) }}</td>
            <!-- <td>
              <span v-if="moment.start_garantie" class="start-garantie-badge">Ja</span>
              <span v-else>-</span>
            </td> -->
            <td><button class="inschrijven-btn" @click="$emit('inschrijven', moment)">Inschrijven</button></td>
          </tr>
        </tbody>
      </table>

      <!-- Mobile card view -->
      <div class="mobile-moments mobile-only">
        <div v-for="moment in moments" :key="moment.id" class="moment-card">
          <div class="moment-header">
            <div class="moment-date">{{ formatDate(moment.start_date) }}</div>
            <div class="moment-time">{{ moment.start_time || '--:--' }}</div>
          </div>
          <div class="moment-location">{{ moment.city || moment.location || 'N.v.t.' }}</div>
          <div class="moment-price">
            <template v-if="moment.price?.discount && moment.price?.original">
              <span class="original-price">{{ formatPrice(moment.price.original) }}</span>
              <span class="discounted-price">{{ formatPrice(moment.price.total) }}</span>
              <span class="discount-badge-mobile">ACTIE</span>
            </template>
            <template v-else>
              {{ formatPrice(moment.price) }}
            </template>
          </div>
          <div class="moment-spots">{{ formatAvailableSpots(moment.available_spots) }}</div>
          <button class="inschrijven-btn mobile-btn" @click="$emit('inschrijven', moment)">Inschrijven</button>
        </div>
      </div>

      <div v-if="moments.length === 0" class="no-moments">Geen passende data gevonden? Probeer een andere datum en/of plaats of neem contact met ons op.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps } from 'vue'

const props = defineProps<{
  moments: any[],
  isLoading: boolean,
  hasError: boolean,
  errorMessage: string
}>()

defineEmits(['retry', 'inschrijven'])

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (price: any) => {
  // console.log('MiniCourseMoments: formatPrice called with:', price)
  
  // Als price een object is met total property
  if (price && typeof price === 'object' && price.total !== undefined) {
    const total = Number(price.total)
    if (!isNaN(total) && total > 0) {
      return `€ ${total.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })},-`
    }
  }
  
  // Als price een directe waarde is (number of string)
  if (price !== null && price !== undefined) {
    const numPrice = Number(price)
    if (!isNaN(numPrice) && numPrice > 0) {
      return `€ ${numPrice.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })},-`
    }
  }
  
  // Als price een object is met andere mogelijke properties
  if (price && typeof price === 'object') {
    // Probeer verschillende mogelijke properties
    const possibleProps = ['amount', 'value', 'price', 'cost', 'total_price', 'price_total']
    for (const prop of possibleProps) {
      if (price[prop] !== undefined) {
        const numPrice = Number(price[prop])
        if (!isNaN(numPrice) && numPrice > 0) {
          return `€ ${numPrice.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })},-`
        }
      }
    }
  }
  
  console.log('MiniCourseMoments: No valid price found, returning "-"')
  return '-'
}

const formatAvailableSpots = (spots: any) => {
  if (spots === null || spots === undefined) {
    return '-'
  }
  
  const numSpots = Number(spots)
  if (isNaN(numSpots)) {
    return '-'
  }
  
  if (numSpots === 0) {
    return 'Vol'
  }
  
  if (numSpots === 1) {
    return '1 plaats'
  }
  
  return `${numSpots} plaatsen`
}
</script>

<style scoped>
.mini-course-moments {
  margin-top: 2rem;
}
.moments-table-wrapper {
  border: 1px solid #b4b4b4;
  border-radius: 5px;
  overflow: hidden;
  width: 100%;
}

/* Desktop table styles */
table th,
table td {
  border: none;
}
.moments-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
}
.moments-table th,
.moments-table td {
  background: #fff;
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 14px;
  border-bottom: 1px solid #b4b4b4 !important;
}
.moments-table th:not(:last-child),
.moments-table td:not(:last-child) {
  border-right: 1px solid #b4b4b4 !important;
}
.moments-table tbody tr:last-child td {
  border-bottom: none !important;
}
.moments-table th {
  color: #0099e3;
  font-weight: 700;
  font-size: 14px;
  /* border-bottom-width: 2px; */
}

/* Mobile card styles */
.mobile-moments {
  padding: 1rem;
}
.moment-card {
  background: #fff;
  border-bottom: 1px solid #b4b4b4;
  padding: 1rem;
  margin-bottom: 1.5rem;
}
.moment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}
.moment-date {
  font-weight: 400;
  color: #3C3C3B;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
}
.moment-time {
  font-weight: 400;
  color: #3C3C3B;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
}
.moment-location {
  font-weight: 400;
  color: #3C3C3B;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  margin-bottom: 10px;
}
.moment-price {
  font-weight: 400;
  color: #3C3C3B;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  margin-bottom: 10px;
}
.moment-spots {
  font-weight: 400;
  color: #3C3C3B;
  font-size: 14px;
  font-family: 'Montserrat', sans-serif;
  text-align: center;
  margin-bottom: 10px;
}
.moment-garantie {
  margin-bottom: 0.5rem;
}

.inschrijven-btn {
  background: #ff6600;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1.2rem;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
}
.inschrijven-btn:hover {
  background: #e05500;
}
.inschrijven-btn.mobile-btn {
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
}

.loading-container,
.error-container,
.no-moments {
  text-align: center;
  margin: 2rem 0;
}
.loading-spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0099e3;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem auto;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.location-column {
  min-width: 180px;
}
.start-garantie-badge {
  background-color: #d4edda;
  color: #155724;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.8rem;
}

/* Price and discount badge styles */
.price-with-badge {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.original-price {
  text-decoration: line-through;
  color: #666;
  font-size: 0.9em;
}

.discounted-price {
  color: #ff6600;
  font-weight: 600;
}

.discount-badge {
  background-color: #ff6600;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.7rem;
  display: inline-block;
  width: fit-content;
  text-align: center;
}

.discount-badge-mobile {
  background-color: #ff6600;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.8rem;
  display: inline-block;
  margin-left: 0.5rem;
}

/* Responsive breakpoints */
@media (max-width: 1200px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
}

@media (min-width: 1201px) {
  .desktop-only {
    display: table;
  }
  .mobile-only {
    display: none;
  }
}
</style> 